/**
 * DualInfoClient - Transparent routing wrapper for InfoClient
 * Routes current state queries to remote node, historical to public API
 * 
 * Dynamic Polling Feature:
 * - When remote node fails, isUsingFallback becomes true
 * - Consumers should poll at 30s instead of 2s when isUsingFallback is true
 * - Health check runs every 30s to detect remote node recovery
 * - When remote recovers, isUsingFallback becomes false and consumers resume 2s polling
 */
export class DualInfoClient {
    constructor(publicClient, remoteClient, useRemote = false) {
        this.publicClient = publicClient;
        this.remoteClient = remoteClient;
        this.useRemote = useRemote;
        this.stats = { remote: 0, public: 0, fallbacks: 0 };
        
        // Dynamic polling state
        this.isUsingFallback = false;           // True when remote node is down, using public API
        this.fallbackListeners = new Set();     // Callbacks for fallback state changes
        this.healthCheckInterval = null;        // Interval for checking remote node recovery
        this.consecutiveFailures = 0;           // Track consecutive remote failures
        this.FAILURE_THRESHOLD = 2;             // Enter fallback mode after 2 consecutive failures
        
        // Log initialization with origin and remote node URL (commented out - spammy on reconnects)
        // const origin = window.location.origin;
        // const remoteNodeUrl = localStorage.getItem('remoteNodeUrl') || 'NOT SET';
        
        // console.log('═══════════════════════════════════════════════════');
        // console.log('🔧 [DUAL-INFO-CLIENT] INITIALIZATION');
        // console.log('═══════════════════════════════════════════════════');
        // console.log(`📍 Origin: ${origin}`);
        // console.log(`🌐 Remote Node URL: ${remoteNodeUrl}`);
        // console.log(`⚙️  Mode: ${useRemote ? '🟢 REMOTE NODE ENABLED' : '🔴 PUBLIC API ONLY'}`);
        // console.log(`📦 publicClient:`, publicClient ? 'PROVIDED' : 'NULL');
        // console.log(`📦 remoteClient:`, remoteClient ? 'PROVIDED' : 'NULL');
        
        // if (useRemote) {
        //     console.log('📋 Routing:');
        //     console.log('   ├─ Current state queries → REMOTE NODE');
        //     console.log('   └─ Historical data → PUBLIC API');
        // } else {
        //     console.log('📋 All queries → PUBLIC HYPERLIQUID API');
        // }
        // console.log('═══════════════════════════════════════════════════');
        
        // Methods that query CURRENT state → REMOTE NODE (with fallback to public API)
        // Based on official node docs: https://github.com/hyperliquid-dex/node#evm-and-info-servers
        const remoteNodeMethods = [
            'clearinghouseState',
            'spotClearinghouseState',
            'openOrders',
            'meta',
            'perpDexs',
            'spotMeta',
            'exchangeStatus',
            'maxBuilderFee'
        ];
        
        // Methods that query HISTORICAL data or require asset indexing → PUBLIC API (always)
        // Note: l2Book explicitly NOT supported on local nodes per official docs
        const publicApiMethods = [
            'l2Book',           // NOT supported on local nodes (requires asset indexing)
            'allMids',          // NOT supported on local nodes (requires indexing all assets)
            'candleSnapshot',   // Historical data
            'userFillsByTime',  // Historical data
            'referral'          // External data
        ];
        
        // Create routing methods for remote node queries
        remoteNodeMethods.forEach(method => {
            this[method] = async (...args) => {
                const startTime = Date.now();
                
                // When in fallback mode, skip remote and use public directly to avoid latency
                if (this.isUsingFallback && this.publicClient) {
                    try {
                        const result = await this.publicClient[method](...args);
                        const duration = Date.now() - startTime;
                        this.stats.public++;
                        // console.log(`✅ [DUAL-INFO-CLIENT] ${method}() via PUBLIC API (fallback mode) (${duration}ms)`);
                        return result;
                    } catch (error) {
                        console.error(`❌ [DUAL-INFO-CLIENT] ${method}() failed on PUBLIC API (fallback mode)`);
                        throw error;
                    }
                }
                
                const source = this.useRemote ? 'REMOTE NODE' : 'PUBLIC API';
                
                // console.log(`🌐 [DUAL-INFO-CLIENT] ${method}() → ${source}`);
                
                try {
                    const client = this.useRemote ? this.remoteClient : this.publicClient;
                    const result = await client[method](...args);
                    const duration = Date.now() - startTime;
                    
                    if (this.useRemote) {
                        this.stats.remote++;
                        // Remote succeeded - reset failure counter
                        this.consecutiveFailures = 0;
                    } else {
                        this.stats.public++;
                    }
                    
                    // console.log(`✅ [DUAL-INFO-CLIENT] ${method}() completed via ${source} (${duration}ms)`);
                    
                    return result;
                } catch (error) {
                    const duration = Date.now() - startTime;
                    
                    // console.error(`❌ [DUAL-INFO-CLIENT] ${method}() failed on ${source} (${duration}ms)`);
                    // console.error(`   Error: ${error.message}`);
                    
                    // Fallback to public API if remote fails
                    if (this.useRemote && this.publicClient) {
                        this.consecutiveFailures++;
                        this.stats.fallbacks++;
                        console.warn(`🔄 [DUAL-INFO-CLIENT] Falling back to PUBLIC API for ${method}()... (failure #${this.consecutiveFailures})`);
                        
                        // Enter fallback mode after threshold consecutive failures
                        if (this.consecutiveFailures >= this.FAILURE_THRESHOLD && !this.isUsingFallback) {
                            this._enterFallbackMode();
                        }
                        
                        try {
                            const fallbackStart = Date.now();
                            const result = await this.publicClient[method](...args);
                            const fallbackDuration = Date.now() - fallbackStart;
                            
                            this.stats.public++;
                            console.log(`✅ [DUAL-INFO-CLIENT] ${method}() fallback SUCCESS via PUBLIC API (${fallbackDuration}ms)`);
                            
                            return result;
                        } catch (fallbackError) {
                            console.error(`💥 [DUAL-INFO-CLIENT] ${method}() fallback FAILED on PUBLIC API`);
                            console.error(`   Fallback Error: ${fallbackError.message}`);
                            throw fallbackError;
                        }
                    }
                    throw error;
                }
            };
        });
        
        // Create methods that ALWAYS use public API (historical data, no remote node support)
        publicApiMethods.forEach(method => {
            this[method] = async (...args) => {
                const startTime = Date.now();
                // console.log(`🌐 [DUAL-INFO-CLIENT] ${method}() → PUBLIC API (historical)`);
                
                try {
                    const result = await this.publicClient[method](...args);
                    const duration = Date.now() - startTime;
                    
                    this.stats.public++;
                    // console.log(`✅ [DUAL-INFO-CLIENT] ${method}() completed via PUBLIC API (${duration}ms)`);
                    
                    return result;
                } catch (error) {
                    const duration = Date.now() - startTime;
                    console.error(`❌ [DUAL-INFO-CLIENT] ${method}() failed on PUBLIC API (${duration}ms)`);
                    console.error(`   Error: ${error.message}`);
                    throw error;
                }
            };
        });
    }
    
    /**
     * Get health status of remote node
     */
    async healthCheck() {
        if (!this.useRemote) {
            return { healthy: false, reason: 'Remote node not enabled' };
        }
        
        try {
            const start = Date.now();
            await this.remoteClient.meta();
            const latency = Date.now() - start;
            
            return {
                healthy: true,
                latency
            };
        } catch (error) {
            return {
                healthy: false,
                reason: error.message
            };
        }
    }
    
    /**
     * Get recommended polling interval based on fallback state
     * @returns {number} - 2000ms for normal, 30000ms for fallback mode
     */
    getPollingInterval() {
        return this.isUsingFallback ? 30000 : 2000;
    }
    
    /**
     * Subscribe to fallback state changes
     * @param {Function} callback - Called with (isUsingFallback, recommendedInterval)
     * @returns {Function} - Unsubscribe function
     */
    onFallbackChange(callback) {
        this.fallbackListeners.add(callback);
        return () => this.fallbackListeners.delete(callback);
    }
    
    /**
     * Enter fallback mode - switch to slower polling
     * @private
     */
    _enterFallbackMode() {
        if (this.isUsingFallback) return;
        
        this.isUsingFallback = true;
        console.warn('⚠️ [DUAL-INFO-CLIENT] ENTERING FALLBACK MODE - Remote node down, switching to 30s polling');
        console.log('📊 Stats at fallback entry:', this.stats);
        
        // Notify all listeners
        const interval = this.getPollingInterval();
        this.fallbackListeners.forEach(cb => {
            try {
                cb(true, interval);
            } catch (e) {
                console.error('[DUAL-INFO-CLIENT] Fallback listener error:', e);
            }
        });
        
        // Start health check interval to detect recovery
        this._startHealthCheck();
    }
    
    /**
     * Exit fallback mode - resume normal polling
     * @private
     */
    _exitFallbackMode() {
        if (!this.isUsingFallback) return;
        
        this.isUsingFallback = false;
        this.consecutiveFailures = 0;
        console.log('✅ [DUAL-INFO-CLIENT] EXITING FALLBACK MODE - Remote node recovered, resuming 2s polling');
        console.log('📊 Stats at recovery:', this.stats);
        
        // Stop health check interval
        this._stopHealthCheck();
        
        // Notify all listeners
        const interval = this.getPollingInterval();
        this.fallbackListeners.forEach(cb => {
            try {
                cb(false, interval);
            } catch (e) {
                console.error('[DUAL-INFO-CLIENT] Fallback listener error:', e);
            }
        });
    }
    
    /**
     * Start periodic health check to detect remote node recovery
     * @private
     */
    _startHealthCheck() {
        if (this.healthCheckInterval) return;
        
        console.log('🔍 [DUAL-INFO-CLIENT] Starting health check interval (every 30s)');
        
        this.healthCheckInterval = setInterval(async () => {
            try {
                const health = await this.healthCheck();
                if (health.healthy) {
                    console.log(`🩺 [DUAL-INFO-CLIENT] Health check: Remote node RECOVERED (latency: ${health.latency}ms)`);
                    this._exitFallbackMode();
                } else {
                    console.log(`🩺 [DUAL-INFO-CLIENT] Health check: Remote node still down (${health.reason})`);
                }
            } catch (e) {
                console.log(`🩺 [DUAL-INFO-CLIENT] Health check error: ${e.message}`);
            }
        }, 30000); // Check every 30 seconds
    }
    
    /**
     * Stop health check interval
     * @private
     */
    _stopHealthCheck() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
            console.log('🔍 [DUAL-INFO-CLIENT] Health check interval stopped');
        }
    }
    
    /**
     * Clean up resources
     */
    destroy() {
        this._stopHealthCheck();
        this.fallbackListeners.clear();
    }
}
